import { ActiveRoom } from '@agent/agent-interface/contact-view/chat/model/active-room';
import { Injectable } from '@angular/core';
import { ChatCopyGuest } from '@commons/chat-assistance/models/chat-copy-guest';
import { distinct, filter, from, map, merge, Observable, Subject, Subscription, tap } from 'rxjs';

import {
  COMETD_CHAT_MEMBER_UPDATE,
  COMETD_CHAT_MESSAGE,
  HttpService,
  Logger,
  MessagingService,
  Option,
  Profile,
  ProfileFacade,
  randomUUID,
  StorageService,
  TranslateService,
  UntilDestroy,
  untilDestroyed,
} from '@contact/upgrade';

import { ChatGuest, IChatGuest } from './models/chat-guest';
import { ChatGuestUpdate } from './models/chat-guest-update';
import { MemberUpdateDto, TYPE_ADD } from './models/chat-member-update';
import { ChatMessage, IChatMessage } from './models/chat-message';
import { ChatRoom, IChatRoom } from './models/chat-room';

const CHAT_REST_PREFIX = 'protected/chat/assistance';

const CHAT_HISTORY_STORAGE_KEY = 'chat-history';
const CHAT_ROOMS_STORAGE_KEY = 'chat-rooms';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ChatAssistanceService {
  private rooms: { [id: string]: IChatRoom } = {};
  private history: IChatMessage[] = [];
  private guestsDirectory: IChatGuest[] = [];

  private _message$: Observable<IChatMessage>;
  private _agentMessage$: Observable<IChatMessage>;

  private _agentMessageSubject = new Subject<IChatMessage>();
  private _subAgentMessage: Subscription;
  private _subMessage: Subscription;

  private _nbUnreadMessages: number = 0;
  private isConnected: boolean = false;

  private _profile: Profile;

  isFlipped: boolean;

  constructor(
    private readonly logger: Logger,
    private readonly translateService: TranslateService,
    private readonly httpService: HttpService,
    private readonly messaging: MessagingService,
    private readonly storage: StorageService,
    private readonly profileFacade: ProfileFacade
  ) {
    this.profileFacade.profile$.pipe(untilDestroyed(this)).subscribe(profile => (this._profile = profile));
  }

  connect(): void {
    if (this.isConnected) {
      return;
    }
    this.isConnected = true;
    const timestampFormat = this.translateService.instant('commons:chat.timestamp_format');

    this.logger.info(`[chat] Connecting...`);
    this.logger.debug(`[chat] timestamp format : ${timestampFormat}`);

    this.loadRooms();
    this.history = this.storage.get(CHAT_HISTORY_STORAGE_KEY).getOrElse([]);

    this.httpService.post(CHAT_REST_PREFIX + '/available', null).subscribe({
      error: _ => (this.isConnected = false),
      complete: () => {
        this.messaging
          .forRemote()
          .listen$<any>(COMETD_CHAT_MEMBER_UPDATE)
          .subscribe(member => this.fromGuestUpdate(member));
        this._message$ = this.messaging.forRemote().listen$<any>(COMETD_CHAT_MESSAGE);
        this._agentMessage$ = this._agentMessageSubject.asObservable();
        this._subMessage = this.fromMessage(this._message$);
        this._subAgentMessage = this.fromMessage(this._agentMessage$);
        this.logger.info('[chat] Connected.');
      },
    });
  }

  disconnect(): void {
    if (!this.isConnected) {
      return;
    }
    this.logger.info(`[chat] Disconnecting...`);
    this.isConnected = false;
    this.httpService.post(`${CHAT_REST_PREFIX}/noMoreAvailable`, null).subscribe({
      error: _ => (this.isConnected = true),
      complete: () => {
        this.messaging.closeChannel(COMETD_CHAT_MESSAGE).subscribe();
        this.messaging.closeChannel(COMETD_CHAT_MEMBER_UPDATE).subscribe();
        this._subMessage.unsubscribe();
        this._subAgentMessage.unsubscribe();
        this.logger.info('[chat] Disconnected.');
      },
    });
  }

  create(): string {
    let roomId = this.createRoom(randomUUID()).id;
    this.saveRooms();
    return roomId;
  }

  createFromId(id: string): string {
    let roomId = this.createRoom(id).id;
    this.saveRooms();
    return roomId;
  }

  open(roomId: string = randomUUID()): Observable<IChatMessage> {
    return this.getRoom(roomId)
      .map(room => this.buildWindowSource(room))
      .get();
  }

  close(roomId: string): void {
    this.removeRoom(roomId);
  }

  getRooms(): IChatRoom[] {
    let result: IChatRoom[] = [];
    for (let roomId in this.rooms) {
      result.push(this.rooms[roomId]);
    }
    return result;
  }

  send(roomId: string, messageContent: string, copyGuest?: ChatCopyGuest): void {
    this.logger.debug(`[chat] Sending to room with identifier[${roomId}] following message [${messageContent}]...`);
    this.createMessage(roomId, this.replaceImageAttributes(messageContent), copyGuest)
      .map(message => ChatMessage.toDto(message))
      .map(message => JSON.stringify(message))
      .forEach(message => {
        this.httpService
          .withHeaders({
            'Content-Type': 'text/plain; charset=UTF-8',
          })
          .post(CHAT_REST_PREFIX + '/sendMessage', message)
          // injects successfully posted message into messaging in way to simulate that the message has been push from the middle.
          .subscribe(() => this._agentMessageSubject.next(ChatMessage.fromDto(JSON.parse(message))));
      });
  }

  private replaceImageAttributes(textContent: string) {
    return textContent.replace(/<img.*?alt="(.*?)"[^\>]+>/g, x => {
      let div = document.createElement('div');
      div.innerHTML = x;
      return ':' + div.getElementsByTagName('img')[0].alt + ':';
    });
  }

  getGuestsDirectory(): Observable<any[]> {
    return this.httpService.get<IChatGuest[]>(CHAT_REST_PREFIX + '/getContacts').pipe(map(guests => (this.guestsDirectory = guests)));
  }

  addGuest(roomId: string, guest: IChatGuest): void {
    this.addGuestToRoom(roomId, ChatGuest.fromDto(guest));
  }

  notifyGuestsAdded(roomId: string, currentGuests: IChatGuest[], addedGuests: IChatGuest[]) {
    let membersToNotify: Array<string> = currentGuests.map(m => m.id);

    addedGuests.forEach(guest => {
      // send update to all members
      this.httpService
        .withHeaders({
          'Content-Type': 'text/plain; charset=UTF-8',
        })
        .post(CHAT_REST_PREFIX + '/memberAdded', MemberUpdateDto.from(guest, membersToNotify, roomId, TYPE_ADD))
        .subscribe(
          tap(() => {
            this.logger.info('Memenber added sent');
          })
        );
    });
  }

  getGuests(roomId: string): IChatGuest[] {
    return this.getRoom(roomId)
      .map(room => room.toGuests)
      .getOrElse([]);
  }

  nbUnreadMessages(roomId?: string): number {
    return this.getRoom(roomId)
      .map(room => room.nbUnreadMessages)
      .getOrElse(this._nbUnreadMessages);
  }

  resetNbUnreadMessages(): void {
    this._nbUnreadMessages = 0;
  }

  canAddGuest(guest: IChatGuest): boolean {
    // filter rooms which contains given guest and for which this guest is the signle one.
    return this.getRooms().filter(room => room.toGuests.map(g => g.id).indexOf(guest.id) >= 0 && room.toGuests.length === 1).length === 0;
  }

  message$(): Observable<any> {
    return this._message$;
  }

  canCopyToNewRoom(): boolean {
    return this.getRooms().length < 4;
  }

  getActiveRooms(): ActiveRoom[] {
    return this.getRooms()
      .filter(room => room.toGuests)
      .map(room => new ActiveRoom(room.id, room.toGuests));
  }

  private createGuest(id: string, firstName: string, lastName: string, mainRole: string): IChatGuest {
    return {
      id: id,
      firstName: firstName,
      lastName: lastName,
      groups: [],
      team: undefined,
      role: mainRole,
      isPresent: true,
    };
  }

  private saveRooms(): void {
    let dataToStore = this.getRooms().map(room => ChatRoom.toDto(room));
    this.storage.store(CHAT_ROOMS_STORAGE_KEY, dataToStore);
  }

  loadRooms(): void {
    this.rooms = this.storage
      .get(CHAT_ROOMS_STORAGE_KEY)
      .map(data => {
        let result: { [roomId: string]: IChatRoom } = {};
        data
          .map(dto => ChatRoom.fromDto(dto))
          .forEach(room => {
            result[room.id] = room;
          });
        return result;
      })
      .getOrElse({});
  }

  private incrNbUnread(roomId: string): number {
    return this.getRoom(roomId)
      .map(room => room.nbUnreadMessages++)
      .sideEffect(() => this.saveRooms())
      .getOrElse(0);
  }

  private decrNbUnread(roomId: string): number {
    return this.getRoom(roomId)
      .filter(room => room.nbUnreadMessages > 0)
      .map(room => room.nbUnreadMessages--)
      .sideEffect(() => this.saveRooms())
      .getOrElse(0);
  }

  private createFromGuest(): IChatGuest {
    let mainRole = 'CSR';
    for (let role of this._profile.roles) {
      if (role === 'SUPERVISOR') {
        mainRole = 'SUPERVISOR';
        break;
      }
    }
    return this.createGuest(this._profile.id, this._profile.firstname, this._profile.lastname, mainRole);
  }

  private getRoom(id?: string): Option<IChatRoom> {
    return Option.of(id).map(i => this.rooms[i]);
  }

  deleteContentConversation(roomId: string): void {
    let newHistory: IChatMessage[] = [];
    this.history
      .filter(msg => msg.roomId !== roomId)
      .forEach(message => {
        newHistory.push(message);
      });
    this.history = newHistory;
    this.storage.store(CHAT_HISTORY_STORAGE_KEY, this.history);
  }

  private createRoom(id: string): IChatRoom {
    return this.getRoom(id).getOrElseFunc(() => {
      this.rooms[id] = {
        id: id,
        fromGuest: this.createFromGuest(),
        toGuests: [],
        nbUnreadMessages: 0,
      };
      this.logger.info(`[chat] Created room with id[${id}]`);
      return this.rooms[id];
    });
  }

  private removeRoom(id: string): void {
    this.getRoom(id).forEach(() => {
      this.rooms[id].toGuests.forEach(e => {
        this.guestsDirectory.filter(guest => guest.id === e.id).forEach(g => (g.isOngoing = false));
      });
      this.deleteContentConversation(id);
      delete this.rooms[id];
      this.saveRooms();
      this.logger.info(`[chat] Removed room with id[${id}]`);
    });
  }

  private addGuestToRoom(roomId: string, guest: IChatGuest): void {
    // trying to get existing room from room identifier
    this.getRoom(roomId)
      .map(room => [room])
      .getOrElseFunc(() => {
        // trying to get existing room from guest identifier
        let rooms = this.findGuestRooms(guest.id);
        if (rooms.length <= 0) {
          // if no room has been found
          rooms = [this.createRoom(roomId)]; // creating new one
        }
        return rooms;
      })
      .forEach(room => {
        // if the guest is not already in the room
        if (room.fromGuest.id !== guest.id && room.toGuests.map(roomGuest => roomGuest.id).indexOf(guest.id) < 0) {
          room.toGuests.push(guest);
          this.logger.info(`[chat] Added guest[${guest.id}, ${guest.firstName} ${guest.lastName}] to room with id[${roomId}].`);
        }
        this.saveRooms();
      });
  }

  private findGuestRooms(guestId: string): IChatRoom[] {
    let result: IChatRoom[] = [];
    for (let roomId in this.rooms) {
      let room = this.rooms[roomId];
      room.toGuests.filter(guest => guest.id === guestId).forEach(() => result.push(room));
    }
    return result;
  }

  private fromGuestUpdate(member: any): void {
    const chatGuest = ChatGuestUpdate.fromDto(member);
    this.logger.debug('[chat] guest update : ' + JSON.stringify(chatGuest));

    this.getGuestsDirectory().subscribe(guestsDirectory => {
      this.guestsDirectory = guestsDirectory;
      this.guestsDirectory
        .filter(guest => guest.id === chatGuest.guestId)
        .forEach(guest => {
          // updating directory's guests availability state
          if (chatGuest.updateType === 'available') {
            guest.isPresent = true;
          } else if (chatGuest.updateType === 'disconnect' || chatGuest.updateType === 'remove') {
            guest.isPresent = false;
          }
        });
      this.guestsDirectory
        .filter(guest => guest.id === chatGuest.guestId)
        .forEach(guest => {
          // sends guest update message to all guest's rooms.
          this.findGuestRooms(guest.id).forEach(room => {
            let message = {
              content: this.translateService.instant(`commons:chat.guest_${chatGuest.updateType}`),
              from: guest,
              direction: 'IN',
              to: [room.fromGuest],
              roomId: room.id,
              timestamp: Date.now(),
              isRead: false,
            } as IChatMessage;
            this._agentMessageSubject.next(message);
          });
        });
    });
  }

  // Returns guests of message excepted current user.
  private guestsOfMessage(message: IChatMessage): IChatGuest[] {
    let result = message.to.filter(guest => guest.id !== this._profile.id);
    result.push(message.from);
    return result.sort((a, b) => (a.id > b.id ? 1 : -1));
  }

  private fromMessage(source: Observable<any>): Subscription {
    return source
      .pipe(
        map(message =>
          // if message contains non empty roomId returns message else creates mapping
          Option.of(message.roomId)
            .map(() => message)
            .getOrElseFunc(() => ChatMessage.fromDto(message))
        ),
        // ensure that the message is for current logged user.
        filter(message => {
          let userId = this._profile.id;
          return message.from.id === userId || message.to.filter(to => to.id === userId).length > 0;
        }),
        distinct(message => message.timestamp)
      )
      .subscribe(message => {
        this._nbUnreadMessages = this._nbUnreadMessages + 1;

        if (!message.isRead) {
          this.incrNbUnread(message.roomId);
        }

        // in case of we receive a message with another that local one built to allow clean history to work
        this.findMessageRooms(message).forEach(room => {
          // find message rooms does not check guests so we check here
          // that all guest from message are the same that matching room found
          // first check size
          let sameSizeOrAllFound: boolean = message.to.length === room.toGuests.length;
          // if same check the guests ids
          if (sameSizeOrAllFound) {
            this.guestsOfMessage(message).forEach(mGuest => {
              this.logger.debug('Check guest from message [' + mGuest.id + '] belongs to matching room [' + room.id + ']');
              if (!room.toGuests.some(rGuest => mGuest.id === rGuest.id)) {
                this.logger.debug('Guest from message [' + mGuest.id + '] does not belongs to matching room [' + room.id + ']');
                sameSizeOrAllFound = false;
              } else {
                this.logger.debug('Guest from message [' + mGuest.id + '] belongs to matching room [' + room.id + ']');
              }
            });
          }
          // if matching room id is diffrent from message room id
          if (message.roomId !== room.id) {
            // we have another room with same guests so replace message id
            if (sameSizeOrAllFound) {
              this.logger.info('Replace received roomId with local one');
              message.roomId = room.id;
            } else {
              // we create a room and guest will be added after
              this.logger.info('Create room with id from received message');
              this.createFromId(message.roomId);
            }
          }
        });

        // only now because a room can have been created updates. add, if needed, guests to room corresponding to the message
        this.guestsOfMessage(message).forEach(guest => this.addGuestToRoom(message.roomId, guest));

        // Saving messages history
        this.history.push(message);
        this.storage.store(CHAT_HISTORY_STORAGE_KEY, this.history);
        this.logger.debug(`[chat] Saved message : ${JSON.stringify(message)}`);
      });
  }

  // finds rooms corresponding to message's room identifier or which contains a guest corresponding to message sender identifier
  private findMessageRooms(message: IChatMessage): IChatRoom[] {
    return this.getRoom(message.roomId)
      .map(room => [room])
      .getOrElseFunc(() => this.findGuestRooms(message.from.id));
  }

  // creates source for a chat window corresponding to given room.
  private buildWindowSource(room: IChatRoom): Observable<IChatMessage> {
    this.logger.debug(`[chat] Building messages source for room with identifier[${room.id}]...`);
    return merge(from(this.history), this._message$, this._agentMessage$).pipe(
      map(message =>
        // if message contains non empty roomId returns message else creates mapping
        Option.of(message.roomId)
          .map(() => message)
          .getOrElseFunc(() => ChatMessage.fromDto(message))
      ),
      filter((message: IChatMessage) => this.findMessageRooms(message).filter(msgRoom => msgRoom.id === room.id).length > 0),
      tap((message: IChatMessage) => {
        if (this._nbUnreadMessages > 0 && !message.isRead) this._nbUnreadMessages = this._nbUnreadMessages - 1;
        message.isRead = true;
        this.decrNbUnread(message.roomId);
      })
    );
  }

  private createMessage(roomId: string, content: string, copyGuest?: ChatCopyGuest): Option<IChatMessage> {
    return this.getRoom(roomId).map(
      room =>
        ({
          content: content,
          from: room.fromGuest,
          copyGuest: copyGuest,
          direction: 'OUT',
          to: room.toGuests,
          roomId: room.id,
          timestamp: Date.now(),
          isRead: true,
        } as IChatMessage)
    );
  }
}
